// @ts-nocheck
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog } from '@/store/dashboard/dashboard.actions';
import {
  setCatalogProvider,
  addCatalogSelectedItem,
  removeCatalogSelectedItem,
  setCatalogSearchQuery,
  setCatalogFilters,
  resetCatalog,
} from '@/store/catalog/catalog.actions';
import { useCatalogUsers, useCatalogProducts, useCreateCatalogItemEntries } from '@/hooks/query/catalog';
import { useItems, useCreateSalesOrderItem, useCreateEstimateItem } from '@/hooks/query';
import { buildLineItemsPayload, buildStagesPayload } from '@/utils/buildSalesOrderItemsPayload';
import { useCatalogProviderConfig, ALL_PROVIDERS } from './useCatalogProviderConfig';
import { getCatalogCallbacks, clearCatalogCallbacks } from './catalogCallbackRegistry';
import { getDraftDocument } from '@/store/draftDocument/draftDocument.registry';
import { useLocation } from 'react-router-dom';
import useApiRequest from '@/hooks/useRequest';
import SupplierVendorMappingDialog from './SupplierVendorMappingDialog';
import InventoryTypeDialog from '@/containers/Dialogs/InventoryTypeDialog';

const REFERENCE_TYPE_MAP: Record<string, string> = {
  'sales-orders': 'SalesOrders',
  'estimates': 'SaleEstimate',
};

// Maps URL path segment to referenceType
const URL_TYPE_MAP: Record<string, string> = {
  'sales-orders': 'SalesOrders',
  'estimates': 'SaleEstimate',
  'invoices': 'SaleInvoice',
  'bills': 'Bill',
};

const CatalogItemSelectionContext = createContext(null);

const toNumber = (value: any, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const buildEspLineItemsPayload = (item: any, espDetail: any) => {
  const detail = espDetail?.data ?? espDetail ?? {};
  const defaultImage =
    detail?.images?.find((image: any) => image?.default)?.url ??
    detail?.images?.[0]?.url ??
    item?.image ??
    '';
  const firstPricing = Array.isArray(detail?.pricing) ? detail.pricing[0] : null;
  const cost = toNumber(firstPricing?.costs?.[0]?.cost, toNumber(item?.cost, 0));
  const price = toNumber(firstPricing?.price, cost);
  const description = String(detail?.description ?? item?.description ?? item?.name ?? '').trim();
  const skuLabel = String(detail?.number ?? item?.number ?? item?.name ?? '').trim();
  const vendorSku = String(detail?.number ?? item?.number ?? '').trim();


  return [
    {
      quantity: 1,
      cost,
      price,
      imageUrl: defaultImage,
      skuLabel,
      description,
      extSell: price,
      margin: cost > 0 && price > 0 ? ((price - cost) / price) * 100 : 0,
      vendorSku,
      variant: {},
      charges:{}

    },
  ];
};

const buildEspStagesPayload = (espDetail: any, vendorName?: string, vendorId?: any) => {
  const detail = espDetail?.data ?? espDetail ?? {};
  const charges = Array.isArray(detail?.charges) ? detail.charges : [];

  const safeVendorName = String(vendorName ?? detail?.supplier?.name ?? '').trim();
  const safeVendorId = vendorId ?? null;

  // If there are no charges, still create a single stage when we have vendor info
  // so backend can persist vendor_name/vendor_id on the stage row.
  if (!charges.length) {
    if (!safeVendorName && !safeVendorId) return [];
    return [
      {
        seq: 0,
        vendorName: safeVendorName,
        vendorId: safeVendorId,
        charges: [],
        decorations: [],
      },
    ];
  }

  return [
    {
      seq: 0,
      vendorName: safeVendorName || (detail?.supplier?.name ?? ''),
      vendorId: safeVendorId,
      charges: charges.map((charge: any) => ({
        name: String(charge?.name ?? '').trim(),
        quantity: toNumber(charge?.quantity, 1),
        cost: toNumber(charge?.cost, 0),
        price: toNumber(charge?.price, 0),
        unitCost: toNumber(charge?.cost, 0),
        unitSell: toNumber(charge?.price, 0),
        extCost: toNumber(charge?.cost, 0) * toNumber(charge?.quantity, 1),
        extSell: toNumber(charge?.price, 0) * toNumber(charge?.quantity, 1),
        lineItemId: 1,
      })),
      decorations: [],
    },
  ];
};

const buildEspItemEntryPayload = (item: any) => {
  const detail = item?.esp_product_detail ?? null;
  const mappedVendor = item?.supplier?.mapping?.contact ?? null;
  const vendorName =
    mappedVendor?.displayName ??
    mappedVendor?.display_name ??
    item?.supplier?.name ??
    detail?.supplier?.name ??
    '';
  const vendorId = mappedVendor?.id ?? null;
  const lineItems = Array.isArray(item?.line_items) ? item.line_items : [];
  const images = Array.isArray(detail?.images) ? detail.images : [];
  const variants = Array.isArray(detail?.variants)
    ? detail.variants.map((variant: any) => ({
        name: variant?.name,
        options: Array.isArray(variant?.options)
          ? variant.options.map((option: any) =>
              typeof option === 'string' ? { name: option } : { name: option?.name ?? String(option ?? '') },
            )
          : [],
        supplierSkus: Array.isArray(variant?.supplierSkus) ? variant.supplierSkus : [],
      }))
    : [];

  return {
    name: item.name,
    cost: item.cost ?? 0,
    description: String(
      item.description ?? item.purchase_description ?? item.sell_description ?? item.name ?? '',
    ).trim(),
    ...(item?.item_source === 'catalog' && lineItems.length ? { line_items: lineItems } : {}),
    ...(images.length ? { images } : {}),
    ...(variants.length ? { variants } : {}),
    ...(detail?.number ? { vendor_item: detail.number } : {}),
    category: {
      source: 'esp',
      productId: detail?.id ?? item?.id ?? null,
      number: detail?.number ?? item?.number ?? null,
      supplier: detail?.supplier ?? item?.supplier ?? null,
      pricing: Array.isArray(detail?.pricing) ? detail.pricing : [],
      charges: Array.isArray(detail?.charges) ? detail.charges : [],
    },
    stages: buildEspStagesPayload(detail, vendorName, vendorId),
  };
};

const buildProductsLineItemsPayload = (item: any) => {
  const skus = Array.isArray(item?.itemSkus ?? item?.item_skus) ? (item?.itemSkus ?? item?.item_skus) : [];

  if (skus.length) {
    const sku = skus[0];
    return [{
      skuId: sku?.id ?? null,
      variant: {},
      quantity: 1,
      cost: toNumber(sku?.cost_price ?? sku?.costPrice, toNumber(item?.cost, 0)),
      price: toNumber(sku?.sell_price ?? sku?.sellPrice, toNumber(item?.sell_price ?? item?.price, 0)),
      imageUrl: sku?.picture_uri ?? sku?.pictureUri ?? item?.image ?? '',
      skuLabel: sku?.sku_label ?? sku?.skuLabel ?? item?.name ?? '',
      description: String(item?.description ?? item?.name ?? '').trim(),
      extSell: toNumber(sku?.sell_price ?? sku?.sellPrice, toNumber(item?.sell_price ?? item?.price, 0)),
      margin: toNumber(sku?.margin, 0),
      vendorSku: sku?.item_sku ?? sku?.itemSku ?? '',
    }];
  }

  return [{
    quantity: 1,
    cost: toNumber(item?.cost, 0),
    price: toNumber(item?.sell_price ?? item?.price, toNumber(item?.cost, 0)),
    imageUrl: item?.image ?? '',
    skuLabel: String(item?.name ?? '').trim(),
    description: String(item?.description ?? item?.name ?? '').trim(),
    extSell: toNumber(item?.sell_price ?? item?.price, toNumber(item?.cost, 0)),
    margin: 0,
    vendorSku: '',
    variant: {},
  }];
};

const buildProductsStagesPayload = (item: any) => {
  const charges = Array.isArray(item?.charges) ? item.charges : [];
  const decorations = Array.isArray(item?.decorations) ? item.decorations : [];

  if (!charges.length && !decorations.length) return [];

  return [{
    seq: 0,
    vendorName: '',
    charges: charges.map((charge: any) => ({
      name: String(charge?.name ?? '').trim(),
      quantity: toNumber(charge?.quantity, 1),
      cost: toNumber(charge?.unitCost ?? charge?.unit_cost ?? charge?.cost, 0),
      price: toNumber(charge?.unitSell ?? charge?.unit_sell ?? charge?.price, 0),
      unitCost: toNumber(charge?.unitCost ?? charge?.unit_cost ?? charge?.cost, 0),
      unitSell: toNumber(charge?.unitSell ?? charge?.unit_sell ?? charge?.price, 0),
      extCost: toNumber(charge?.extCost ?? charge?.ext_cost, toNumber(charge?.unitCost ?? charge?.unit_cost ?? charge?.cost, 0)),
      extSell: toNumber(charge?.extSell ?? charge?.ext_sell, toNumber(charge?.unitSell ?? charge?.unit_sell ?? charge?.price, 0)),
      lineItemId: 1,
    })),
    decorations: decorations.map((decoration: any) => ({
      name: String(decoration?.name ?? decoration?.position ?? 'Decoration').trim(),
      position: String(decoration?.position ?? '').trim(),
      imprint: String(decoration?.imprint ?? '').trim(),
      variant: Array.isArray(decoration?.variant) ? decoration.variant : [],
      quantity: 1,
      cost: toNumber(decoration?.unitCost ?? decoration?.unit_cost ?? decoration?.cost, 0),
      price: toNumber(decoration?.unitSell ?? decoration?.unit_sell ?? decoration?.price, 0),
      hideCost: !!(decoration?.hideCost ?? decoration?.hide_cost),
      hidePrice: !!(decoration?.hidePrice ?? decoration?.hide_price),
      images: decoration?.pictureUri ? [{ url: decoration.pictureUri, default: true }] : [],
      lineItemId: 1,
    })),
  }];
};

const buildProductsItemEntryPayload = (item: any) => ({
  name: item.name,
  code: item.code ?? item.customer_item_number ?? null,
  cost: item.cost ?? 0,
  description: String(item?.description ?? item?.name ?? '').trim(),
  ...(item?.inventoryType ? { inventoryType: item.inventoryType } : {}),
  variants: Array.isArray(item?.variants)
    ? item.variants.map((variant: any) => ({
        name: variant?.name,
        options: Array.isArray(variant?.options)
          ? variant.options.map((option: any) =>
              typeof option === 'string' ? { name: option } : { name: option?.name ?? String(option ?? '') },
            )
          : [],
      }))
    : [],
  images: item?.image ? [{ url: item.image, default: true }] : [],
  line_items: buildProductsLineItemsPayload(item),
  stages: buildProductsStagesPayload(item),
  category: {
    source: 'products',
    productId: item?.id ?? null,
  },
});

const normalizeProductsItemDetail = (item: any, detail: any) => {
  const fullItem = detail ?? {};
  return {
    ...item,
    ...fullItem,
    code: fullItem?.code ?? item?.code ?? null,
    cost: fullItem?.cost_price ?? fullItem?.cost ?? item?.cost ?? 0,
    sell_price: fullItem?.sell_price ?? fullItem?.sellPrice ?? item?.sell_price ?? item?.price ?? fullItem?.cost_price ?? 0,
    description:
      fullItem?.purchase_description ??
      fullItem?.sell_description ??
      fullItem?.description ??
      item?.description ??
      '',
    image: fullItem?.picture_uri ?? fullItem?.image ?? item?.image ?? null,
    variants: fullItem?.variants ?? item?.variants ?? [],
    charges: fullItem?.charges ?? item?.charges ?? [],
    decorations: fullItem?.decorations ?? item?.decorations ?? [],
    itemSkus: fullItem?.itemSkus ?? fullItem?.item_skus ?? fullItem?.skus ?? item?.itemSkus ?? [],
  };
};

const hasCatalogStructurePayload = (item: any) => {
  const hasLineItems = Array.isArray(item?.line_items) && item.line_items.length > 0;
  const hasProductsStages =
    (Array.isArray(item?.charges) && item.charges.length > 0) ||
    (Array.isArray(item?.decorations) && item.decorations.length > 0);
  const hasProviderStages =
    Array.isArray(item?.esp_product_detail?.charges) && item.esp_product_detail.charges.length > 0;
  const hasStages = hasProductsStages || hasProviderStages;
  return item?.item_source === 'catalog' && (hasLineItems || hasStages);
};

export function CatalogItemSelectionProvider({ initialProvider = 'esp', children }) {
  const { onConfirm, onCancel } = getCatalogCallbacks();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const apiRequest = useApiRequest();
  const { selectedItems, searchQuery, filters } = useSelector((s: any) => s.catalog);
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [addingItemIds, setAddingItemIds] = useState<Record<string, boolean>>({});
  const [inventoryTypeDialog, setInventoryTypeDialog] = useState({ isOpen: false, item: null });
  const [vendorMappingDialog, setVendorMappingDialog] = useState({
    isOpen: false,
    item: null,
    supplierId: null,
    supplierName: null,
  });
  const { mutateAsync: createItemEntries } = useCreateCatalogItemEntries();
  const { mutateAsync: createSalesOrderItem } = useCreateSalesOrderItem();
  const { mutateAsync: createEstimateItem } = useCreateEstimateItem();

  const normalizedInitialProvider = useMemo(() => {
    const raw = String(initialProvider ?? '').trim().toLowerCase();
    if (!raw) return 'esp';
    if (raw === 'products catalog' || raw === 'product catalog') return 'products';
    if (raw === 'one source') return 'onesource';
    return raw;
  }, [initialProvider]);

  const [activeProvider, setActiveProvider] = useState(normalizedInitialProvider);

  useEffect(() => {
    setActiveProvider(normalizedInitialProvider);
    dispatch(setCatalogProvider(normalizedInitialProvider));
    setAddingItemIds({});
  }, [dispatch, normalizedInitialProvider]);

  const providerConfig = useCatalogProviderConfig(activeProvider);
  const isProductsCatalog = providerConfig?.key === 'products';

  const {
    data: catalogUsers,
    isLoading: usersLoadingRaw,
    isFetching: usersFetchingRaw,
    refetch: refetchCatalogUsers,
  } = useCatalogUsers(activeProvider, { enabled: !isProductsCatalog });

  const usersLoading = isProductsCatalog ? false : usersLoadingRaw;
  const hasCredentials = isProductsCatalog
    ? true
    : (Array.isArray(catalogUsers?.data) ? catalogUsers.data.length > 0 : !!catalogUsers?.data?.id);

  const { data: productsData, isLoading: productsLoadingRaw } = useCatalogProducts(
    activeProvider,
    { q: searchQuery, ...filters },
    { enabled: !isProductsCatalog && hasCredentials },
  );

  const {
    data: { items: productsItems } = { items: [] },
    isLoading: itemsLoading,
    isFetching: itemsFetching,
  } = useItems(
    { page_size: 10000, ...(searchQuery ? { search: searchQuery, search_column: 'name' } : {}) },
    { enabled: isProductsCatalog },
  );

  const mappedProducts = useMemo(() => {
    if (!isProductsCatalog) return productsData?.data ?? [];
    return (productsItems ?? []).map((item) => ({
      id: item.id,
      name: item.name,
      cost: item.cost_price ?? item.cost ?? 0,
      sell_price: item.sell_price ?? item.sellPrice ?? item.cost_price ?? item.cost ?? 0,
      description: item.purchase_description ?? item.sell_description ?? '',
      image: item.picture_uri ?? item.image ?? null,
      variants: item.variants,
      charges: item.charges ?? [],
      decorations: item.decorations ?? [],
      itemSkus: item.itemSkus ?? item.item_skus ?? item.skus ?? [],
      type: item.type
    }));
  }, [isProductsCatalog, productsData, productsItems]);

  const productsLoading = isProductsCatalog ? (itemsLoading || itemsFetching) : productsLoadingRaw;

  const handleProviderChange = useCallback((key: string) => {
    setActiveProvider(key);
    dispatch(setCatalogProvider(key));
  }, [dispatch]);

  const addItemInternal = useCallback(async (item: any) => {
    const rawId = item?.id ?? null;
    const itemId = rawId != null ? String(rawId) : null;
    if (itemId) {
      setAddingItemIds((prev) => ({ ...(prev ?? {}), [itemId]: true }));
    }

    const normalizedItem = {
      ...item,
      item_source: 'catalog',
      catalog_provider: activeProvider,
    };

    try {
      if (activeProvider === 'products') {
        const response = await apiRequest.get(`items/${item.id}`);
        const fullItem = response?.data?.item ?? null;
        dispatch(addCatalogSelectedItem(normalizeProductsItemDetail(normalizedItem, fullItem)));
        return;
      }

      if (!['esp', 'sage', 'onesource'].includes(activeProvider)) {
        dispatch(addCatalogSelectedItem(normalizedItem));
        return;
      }

      const response = await apiRequest.get(`catalog/${activeProvider}/product/${item.id}`);
      const providerResponse = response?.data?.data;
      const productDetail = providerResponse?.data;
      const providerError = providerResponse?.error;
      const mappedVendorName =
        normalizedItem?.supplier?.mapping?.contact?.displayName ??
        normalizedItem?.supplier?.mapping?.contact?.display_name ??
        null;
      const patchedDetail =
        mappedVendorName && productDetail?.supplier
          ? {
              ...productDetail,
              supplier: {
                ...productDetail.supplier,
                name: mappedVendorName,
              },
            }
          : productDetail;

      dispatch(addCatalogSelectedItem({
        ...normalizedItem,
        esp_product_detail: patchedDetail ?? null,
        line_items: providerError?.isError ? [] : buildEspLineItemsPayload(item, patchedDetail),
      }));
    } catch (error) {
      dispatch(addCatalogSelectedItem({
        ...normalizedItem,
        ...(activeProvider === 'products'
          ? {}
          : { line_items: [] }),
      }));
    } finally {
      if (itemId) {
        setAddingItemIds((prev) => {
          const next = { ...(prev ?? {}) };
          delete next[itemId];
          return next;
        });
      }
    }
  }, [activeProvider, apiRequest, dispatch]);

  const handleCloseVendorMappingDialog = useCallback(() => {
    setVendorMappingDialog({ isOpen: false, item: null, supplierId: null, supplierName: null });
  }, []);

  const handleCloseInventoryTypeDialog = useCallback(() => {
    setInventoryTypeDialog({ isOpen: false, item: null });
  }, []);

  const handleConfirmInventoryTypeDialog = useCallback(async (inventoryType: 'ostk' | 'sinv') => {
    const pendingItem = inventoryTypeDialog.item;
    setInventoryTypeDialog({ isOpen: false, item: null });
    if (!pendingItem) return;
    return addItemInternal({ ...pendingItem, inventoryType });
  }, [inventoryTypeDialog, addItemInternal]);

  const handleConfirmVendorMappingDialog = useCallback(async ({ vendorId, externalVendorId: overrideExternalVendorId, externalVendorName: overrideExternalVendorName }) => {
    const pendingItem = vendorMappingDialog.item;
    const externalVendorId = overrideExternalVendorId ?? vendorMappingDialog.supplierId;
    const externalVendorName = overrideExternalVendorName ?? vendorMappingDialog.supplierName;

    if (!pendingItem || !externalVendorId || !vendorId) {
      handleCloseVendorMappingDialog();
      return;
    }

    try {
      const res = await apiRequest.post(
        `catalog/vendor-mappings/${activeProvider}/assign`,
        {
          externalVendorId,
          externalVendorName,
          contactId: vendorId,
        }
      );
      const mapping = res?.data?.data ?? null;
      const mappedVendorName =
        mapping?.contact?.displayName ??
        mapping?.contact?.display_name ??
        null;

      handleCloseVendorMappingDialog();
      return addItemInternal({
        ...pendingItem,
        supplier: {
          ...(pendingItem?.supplier ?? {}),
          name: mappedVendorName || (pendingItem?.supplier?.name ?? externalVendorName),
          mapping,
        },
      });
    } catch (error) {
      handleCloseVendorMappingDialog();
      return addItemInternal(pendingItem);
    }
  }, [activeProvider, apiRequest, vendorMappingDialog, handleCloseVendorMappingDialog, addItemInternal]);

  const handleAddItem = useCallback(async (item: any) => {
    const rawId = item?.id ?? null;
    const itemId = rawId != null ? String(rawId) : null;
    if (itemId) {
      setAddingItemIds((prev) => ({ ...(prev ?? {}), [itemId]: true }));
    }

    const clearAdding = () => {
      if (!itemId) return;
      setAddingItemIds((prev) => {
        const next = { ...(prev ?? {}) };
        delete next[itemId];
        return next;
      });
    };

    try {
      if (activeProvider === 'products') {
        clearAdding();
        if (item?.type == 'inventory') {
          setInventoryTypeDialog({ isOpen: true, item });
        } else {
          addItemInternal(item);
        }
        return;
      }

      if (!['esp', 'sage', 'onesource'].includes(activeProvider)) {
        clearAdding();
        return addItemInternal(item);
      }

      const supplier = item?.supplier ?? null;
      const externalVendorId =
        String(supplier?.asiNumber ?? supplier?.id ?? '').trim() || null;
      const externalVendorName = supplier?.name ?? null;
      const mappedVendorName = supplier?.mapping?.contact?.displayName ?? null;

      if (!externalVendorId) {
        clearAdding();
        return addItemInternal(item);
      }

      if (mappedVendorName) {
        clearAdding();
        return addItemInternal(item);
      }

      // Double-check DB mapping before prompting user.
      try {
        const res = await apiRequest.get(
          `catalog/vendor-mappings/${activeProvider}/lookup`,
          { params: { external_vendor_id: externalVendorId } }
        );
        const mapping = res?.data?.data ?? null;
        const dbMappedName =
          mapping?.contact?.displayName ??
          mapping?.contact?.display_name ??
          null;

        if (dbMappedName) {
          clearAdding();
          return addItemInternal({
            ...item,
            supplier: {
              ...supplier,
              name: dbMappedName,
              mapping,
            },
          });
        }
      } catch (e) {
        // ignore and fall back to manual mapping dialog
      }

      setVendorMappingDialog({
        isOpen: true,
        item,
        supplierId: externalVendorId,
        supplierName: externalVendorName,
      });
      clearAdding();
    } catch (error) {
      clearAdding();
      return addItemInternal(item);
    }
  }, [activeProvider, apiRequest, addItemInternal]);

  const handleRemoveItem = useCallback((itemId: any) => {
    dispatch(removeCatalogSelectedItem(itemId));
    const key = itemId != null ? String(itemId) : null;
    if (key) {
      setAddingItemIds((prev) => {
        if (!prev?.[key]) return prev;
        const next = { ...(prev ?? {}) };
        delete next[key];
        return next;
      });
    }
  }, [dispatch]);

  const handleSearchChange = useCallback((q: string) => {
    dispatch(setCatalogSearchQuery(q));
  }, [dispatch]);

  const handleFiltersChange = useCallback((f: Record<string, any>) => {
    dispatch(setCatalogFilters(f));
  }, [dispatch]);

  const handleConfirm = useCallback(async () => {
    setIsConfirming(true);
    try {
      const enrichedSelectedItems = await Promise.all(
        selectedItems.map(async (item) => {
          if (item?.catalog_provider !== 'products') return item;

          try {
            const response = await apiRequest.get(`items/${item.id}`);
            const fullItem = response?.data?.item ?? null;
            return normalizeProductsItemDetail(item, fullItem);
          } catch (error) {
            return item;
          }
        }),
      );

      const draft = getDraftDocument();
      // For existing documents (edit mode), fall back to URL
      const urlMatch = pathname.match(/^\/([^/]+)\/(\d+)/);
      const referenceId = draft?.id ?? (urlMatch ? Number(urlMatch[2]) : null);
      const referenceType = draft?.type
        ? REFERENCE_TYPE_MAP[draft.type]
        : (urlMatch ? URL_TYPE_MAP[urlMatch[1]] : null);

      const res = await createItemEntries(
        enrichedSelectedItems.map((item) => ({
          ...(item?.item_source === 'catalog' && item?.catalog_provider === 'products'
            ? buildProductsItemEntryPayload(item)
            : item?.item_source === 'catalog' && item?.esp_product_detail
            ? buildEspItemEntryPayload(item)
            : {
                name: item.name,
                cost: item.cost ?? 0,
                description: String(
                  item.description ?? item.purchase_description ?? item.sell_description ?? item.name ?? '',
                ).trim(),
                variants: Array.isArray(item.variants)
                  ? item.variants.map((v: any) => ({
                      name: v.name,
                      options: (v.options ?? []).map((o: any) => ({ name: o.name })),
                    }))
                  : [],
              }),
          ...(referenceId ? { referenceId } : {}),
          ...(referenceType ? { referenceType } : {}),
        }))
      );
      const createdEntries = res.data.data;

      if (referenceId && referenceType) {
        const docPayload = {
          lineItems: buildLineItemsPayload([]),
          stages: buildStagesPayload([], {}, {}, [], {}, ''),
        };
        const docType = draft?.type ?? urlMatch?.[1];
        await Promise.all(
          createdEntries.map((entry, index) => {
            const entryId = Number(entry?.id);
            if (!Number.isFinite(entryId)) return Promise.resolve();
            if (hasCatalogStructurePayload(enrichedSelectedItems[index])) return Promise.resolve();
            if (docType === 'sales-orders') return createSalesOrderItem([referenceId, entryId, docPayload]);
            if (docType === 'estimates') return createEstimateItem([referenceId, entryId, docPayload]);
            return Promise.resolve();
          })
        );
      }

      const enriched = enrichedSelectedItems.map((item, i) => ({ ...createdEntries[i], catalogItem: item }));
      onConfirm?.(enriched);
    } finally {
      setIsConfirming(false);
      clearCatalogCallbacks();
      dispatch(resetCatalog());
      dispatch(closeDialog('catalog-item-selection'));
    }
  }, [onConfirm, pathname, selectedItems, createItemEntries, createSalesOrderItem, createEstimateItem, dispatch]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    clearCatalogCallbacks();
    dispatch(resetCatalog());
    dispatch(closeDialog('catalog-item-selection'));
  }, [onCancel, dispatch]);

  const value = {
    provider: activeProvider,
    providerConfig,
    allProviders: ALL_PROVIDERS,
    selectedItems,
    searchQuery,
    filters,
    hasCredentials,
    usersLoading,
    usersFetching: isProductsCatalog ? false : usersFetchingRaw,
    refetchCredentials: isProductsCatalog ? undefined : refetchCatalogUsers,
    products: mappedProducts,
    productsLoading,
    credentialsDialogOpen,
    setCredentialsDialogOpen,
    isConfirming,
    addingItemIds,
    handleProviderChange,
    handleAddItem,
    handleRemoveItem,
    handleSearchChange,
    handleFiltersChange,
    handleConfirm,
    handleCancel,
  };

  return (
    <CatalogItemSelectionContext.Provider value={value}>
      {children}
      <SupplierVendorMappingDialog
        isOpen={vendorMappingDialog.isOpen}
        supplierId={vendorMappingDialog.supplierId}
        supplierName={vendorMappingDialog.supplierName}
        onClose={handleCloseVendorMappingDialog}
        onConfirm={handleConfirmVendorMappingDialog}
      />
      <InventoryTypeDialog
        isOpen={inventoryTypeDialog.isOpen}
        itemName={inventoryTypeDialog?.item?.name ?? null}
        onClose={handleCloseInventoryTypeDialog}
        onSelect={handleConfirmInventoryTypeDialog}
      />
    </CatalogItemSelectionContext.Provider>
  );
}

export const useCatalogItemSelection = () => useContext(CatalogItemSelectionContext);
